// src/context/WishlistContext.jsx
// Saved items ("wishlist"). Persisted to localStorage the same way the cart is,
// so it survives reloads and works for guests as well as signed-in shoppers.
import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

const WishlistContext = createContext(null);

const STORAGE_KEY = 'folex_tech_wishlist';

export const useWishlist = () => {
  const ctx = useContext(WishlistContext);
  if (!ctx) throw new Error('useWishlist must be used within a WishlistProvider');
  return ctx;
};

const load = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const items = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(items)) return [];
    // Drop anything without a product id — it can never be rendered or removed.
    return items.filter((it) => it && it.productId);
  } catch {
    return [];
  }
};

const save = (items) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch {}
};

// Products arrive in a few shapes (Firestore doc, card props, detail page), so
// normalise once here and store exactly what the wishlist page needs to render.
const normalize = (product) => ({
  productId: product.productId || product.id,
  name: product.name || 'Product',
  brand: product.brand || '',
  category: product.category || '',
  price: product.price ?? 0,
  discountPrice: product.discountPrice ?? product.discount_price ?? null,
  imageUrl:
    product.imageUrl ||
    product.images?.[0] ||
    product.image_url ||
    '',
  stock: product.stock ?? null,
  sizes: Array.isArray(product.sizes) ? product.sizes : [],
  colors: Array.isArray(product.colors) ? product.colors : [],
  addedAt: new Date().toISOString(),
});

export const WishlistProvider = ({ children }) => {
  const [items, setItems] = useState(load);

  const isWishlisted = useCallback(
    (productId) => items.some((i) => i.productId === productId),
    [items]
  );

  const addToWishlist = useCallback((product) => {
    const entry = normalize(product);
    if (!entry.productId) return { success: false };
    setItems((prev) => {
      if (prev.some((i) => i.productId === entry.productId)) return prev;
      const next = [entry, ...prev];
      save(next);
      return next;
    });
    return { success: true };
  }, []);

  const removeFromWishlist = useCallback((productId) => {
    setItems((prev) => {
      const next = prev.filter((i) => i.productId !== productId);
      save(next);
      return next;
    });
    return { success: true };
  }, []);

  // Returns `true` when the product ended up saved, `false` when removed —
  // handy for showing the right toast at the call site.
  const toggleWishlist = useCallback((product) => {
    const id = product.productId || product.id;
    if (!id) return false;
    let added = false;
    setItems((prev) => {
      const exists = prev.some((i) => i.productId === id);
      added = !exists;
      const next = exists
        ? prev.filter((i) => i.productId !== id)
        : [normalize(product), ...prev];
      save(next);
      return next;
    });
    return added;
  }, []);

  const clearWishlist = useCallback(() => {
    setItems([]);
    save([]);
  }, []);

  const value = useMemo(
    () => ({
      wishlist: items,
      wishlistCount: items.length,
      isWishlisted,
      addToWishlist,
      removeFromWishlist,
      toggleWishlist,
      clearWishlist,
    }),
    [items, isWishlisted, addToWishlist, removeFromWishlist, toggleWishlist, clearWishlist]
  );

  return <WishlistContext.Provider value={value}>{children}</WishlistContext.Provider>;
};
